<template>
    <quote-column-info v-model="showSlideout" :column-key="slideoutKey" />
    <asli-quote-display-table
        :headers="multiTableHeaders"
        class="w-full"
        @show-more-info-for-column="showMoreInfoForColumn($event)"
    >
        <template #desktop>
            <asli-table-row v-for="(quote, index) in quoteObjects" :key="quote.info.id">
                <asli-table-cell center v-for="(header, index) in multiTableHeaders">
                    <asli-quote-display-table-quote
                        v-if="header.id === TableHeaderKey.INSURER_LIST"
                        :quote="quote.info"
                        :force-row="false"
                    />
                    <asli-quote-display-table-actions
                        v-else-if="header.id === TableHeaderKey.ACTIONS"
                        :id="quote.info.id"
                        @click-buy-now="selectQuote($event)"
                        @click-more-info="clickMoreInfo($event)"
                    />
                    <asli-link :href="quote.ipid" v-else-if="header.id === TableHeaderKey.IPID">
                        <div class="text-center">
                            <asli-link :href="quote.ipid" class="text-xs">View</asli-link>
                        </div>
                    </asli-link>
                    <asli-icon v-else :icon="getIconForAddon(getAddonKey(header.id, quote))" />
                </asli-table-cell>
            </asli-table-row>
        </template>
        <template #mobile>
            <asli-quote-display-mobile-row
                v-for="(header, index) in multiTableHeaders"
                no-focus
                :header="header"
                center
                :quotes="quoteObjects"
                v-slot="{ quote }"
                @show-more-info-for-column="showMoreInfoForColumn($event)"
            >
                <asli-quote-display-table-quote v-if="header.id === TableHeaderKey.INSURER_LIST" :quote="quote.info" />
                <asli-quote-display-table-actions
                    v-else-if="header.id === TableHeaderKey.ACTIONS"
                    :id="quote.info.id"
                    @click-buy-now="selectQuote($event)"
                    @click-more-info="clickMoreInfo($event)"
                />
                <asli-link v-else-if="header.id === TableHeaderKey.IPID" :href="quote.ipid">View</asli-link>
                <template v-else>
                    <asli-icon :icon="getIconForAddon(getAddonKey(header.id, quote))"></asli-icon>
                </template>
            </asli-quote-display-mobile-row>
        </template>
    </asli-quote-display-table>
</template>

<script lang="ts" setup>
import { Quote } from "@/stores/risk/types/types";
import QuoteColumnInfo from "@/components/QuoteColumnInfo.vue";
import { getFormattedLogoName } from "@/utils";
import {
    AsliQuoteDisplayTable,
    AsliTableCell,
    AsliTableRow,
    AsliIcon,
    getIconForAddon,
    AsliQuoteDisplayTableQuote,
    QuoteDisplayTableProp,
    AsliQuoteDisplayTableActions,
    AsliQuoteDisplayMobileRow,
    AsliLink
} from "@mbs-npm/anpost-ui";
import { PropType, computed } from "vue";
import { multiTableHeaders, getAddonKey } from "./constants";
import { getBenefitsForQuote } from "../helpers/benefits-helpers";
import { useQuoteColumnInfo } from "@/hooks/useQuoteColumnInfo";
import { TableHeaderKey } from "../types";

const props = defineProps({
    quotes: {
        type: Array as PropType<Array<Quote>>,
        required: true
    }
});

const emit = defineEmits<{
    (e: "selectQuote", id: number): void;
    (e: "clickMoreInfo", id: number): void;
}>();

const { showMoreInfoForColumn, showSlideout, slideoutKey } = useQuoteColumnInfo();

const quoteObjects = computed<Array<TableQuoteObject>>(() => {
    return props.quotes.map(quote => {
        const benefits = getBenefitsForQuote(quote.insurer_benefits);
        return {
            info: {
                id: quote.id,
                insurerName: quote.insurer_name,
                logo: getLogoPath(quote.insurer_name ?? ""),
                premium: quote.premium
            },
            ...benefits
        };
    });
});

function getLogoPath(insurer: string) {
    return new URL(`../../../../assets/logos/${getFormattedLogoName(insurer)}.png`, import.meta.url).href;
}

interface TableQuoteObject {
    info: QuoteDisplayTableProp;
    hasBuildingsCovered: boolean;
    hasContentsCovered: boolean;
    hasHomeEmergency: boolean;
    hasFireBrigadeCharges: boolean;
    hasLegalLiability: boolean;
    ipid: string;
}

function selectQuote(quoteId: number) {
    emit("selectQuote", quoteId);
}

function clickMoreInfo(quoteId: number) {
    emit("clickMoreInfo", quoteId);
}
</script>
